import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { required } from '../../../utils/validations';
import { InputWrapper } from '../../common/input-wrapper';
import { AlertService } from '../../../services/alert.service';
import { useModalClose } from '../../../utils/useModalClose';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { RouterInput, trpc } from '../../../api/trpc';

import { DatePicker } from 'rsuite';

import { getStatusEmailCampaign, ItemEmailCampaign } from './EmailCampaignTab';
import {
  formatDateTimeValue,
  parseDateTimeValue,
} from '../../../utils/date-value';
import { useGlobalMutate } from '../../../api/useGlobalMutate';

type Form = {
  name: string;
  comment?: string;
  templateId: string;
  sendPerDayCount: number;
  recipients: string;
  startAt: string;
};

export type ModalProps = {
  close: () => void;
  reject: () => void;
  emailCampaign?: ItemEmailCampaign;
};

export const AddEmailCampaign: React.FC<ModalProps> = ({
  close,
  reject,
  emailCampaign,
}) => {
  const { data } = useSWR(
    emailCampaign?.id != null
      ? ([
          'emailCampaign.emailCampaignStatistic',
          { id: emailCampaign.id },
        ] as const)
      : null,
    () =>
      trpc.emailCampaign.emailCampaignStatistic.query({
        id: emailCampaign!.id!,
      }),
  );
  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
  } = useForm<Form>({
    defaultValues: emailCampaign
      ? {
          name: emailCampaign.name,
          comment: emailCampaign.comment || '',
          templateId: emailCampaign.templateId,
          sendPerDayCount: emailCampaign.sendPerDayCount,
          recipients: emailCampaign.recipients,
          startAt: formatDateTimeValue(emailCampaign.startAt),
        }
      : {},
  });

  const { mutate: globalMutate } = useGlobalMutate();

  const [isStartButtonPressed, setIsStartButtonPressed] = useState(
    emailCampaign ? !emailCampaign.isPaused : false,
  );
  const [isPausedButtonPressed, setIsPausedButtonPressed] = useState(
    emailCampaign?.finishedAt ? false : true,
  );

  const resultStatus = useMemo(() => {
    if (!emailCampaign || !emailCampaign.startAt) {
      return null;
    }

    return getStatusEmailCampaign(
      emailCampaign.startAt,
      emailCampaign.isPaused,
      emailCampaign.recipients,
      emailCampaign._count.sent,
      emailCampaign.finishedAt,
    );
  }, [emailCampaign]);

  const handleStart = () => {
    setIsStartButtonPressed(true);
    setIsPausedButtonPressed(true);
  };

  const handlePause = () => {
    setIsPausedButtonPressed(false);
    setIsStartButtonPressed(false);
  };

  const handleContinue = () => {
    setIsPausedButtonPressed(true);
    setIsStartButtonPressed(true);
  };

  const handleStop = () => {
    setIsStartButtonPressed(false);
    setIsPausedButtonPressed(true);
  };

  const { trigger: triggerUpdate } = useSWRMutation(
    ['emailCampaign.update'],
    (_k, { arg }: { arg: RouterInput['emailCampaign']['update'] }) =>
      trpc.emailCampaign.update.mutate(arg),
  );
  const { trigger: triggerCreate } = useSWRMutation(
    ['emailCampaign.create'],
    (_k, { arg }: { arg: RouterInput['emailCampaign']['create'] }) =>
      trpc.emailCampaign.create.mutate(arg),
  );

  const onSubmit = handleSubmit(async (form) => {
    const sendPerDayCount = Number(form.sendPerDayCount);
    const recipients = form.recipients
      .split(/[\s,]+/)
      .map((num) => num.trim())
      .filter((num, index, self) => self.indexOf(num) === index && num)
      .map((num) => num + ', ')
      .join('')
      .trim();

    const formattedRecipients = recipients.replace(/,\s*$/, '');

    if (emailCampaign?.id) {
      await triggerUpdate({
        id: emailCampaign.id,
        data: {
          name: form.name,
          comment: form.comment,
          templateId: form.templateId,
          sendPerDayCount: sendPerDayCount,
          recipients: formattedRecipients,
          startAt: parseDateTimeValue(form.startAt).toISOString(),
          isPaused: !isStartButtonPressed,
        },
      });
    } else {
      await triggerCreate({
        name: form.name,
        comment: form.comment || '',
        templateId: form.templateId,
        sendPerDayCount: sendPerDayCount,
        recipients: formattedRecipients,
        startAt: parseDateTimeValue(form.startAt).toISOString(),
        isPaused: !isStartButtonPressed,
      });
    }
    AlertService.success();
    close();
    globalMutate();
  });

  const modalRef = useModalClose(reject);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <form className="modal-content" onSubmit={onSubmit}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>
            {emailCampaign?.id ? 'Редактировать' : 'Добавить'} email-рассылку
          </h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => reject()}
            disabled={isSubmitting}
          />
        </div>
        <div className="modal-body p-4">
          <Controller
            control={control}
            name="name"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Название"
                error={fieldState.error?.message}
                required
              >
                <input className="form-control mb-3" type="text" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="comment"
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Комментарий"
                error={fieldState.error?.message}
              >
                <input className="form-control mb-3" type="text" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="templateId"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="ID рыссылки(Unisender)"
                error={fieldState.error?.message}
                required
              >
                <input className="form-control mb-3" type="text" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="sendPerDayCount"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Лимит получателей в сутки"
                error={fieldState.error?.message}
                required
              >
                <input className="form-control mb-3" type="number" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="recipients"
            render={({ field, fieldState }) => (
              <InputWrapper
                className="mb-3"
                title="Добавить участников рассылки"
                error={fieldState.error?.message}
              >
                <div className="input-group">
                  <textarea
                    {...field}
                    className="form-control"
                    rows={4}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
              </InputWrapper>
            )}
          />
          <Controller
            name="startAt"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return 'Выберите дату';

                return true;
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <InputWrapper
                  title="Дата отправки"
                  error={fieldState.error?.message}
                  className="mt-3"
                >
                  <DatePicker
                    className={'w-100'}
                    format={'dd.MM.yyyy HH:mm'}
                    isoWeek={true}
                    onChange={(v) => {
                      if (v === null) {
                        field.onChange(null);
                      } else {
                        field.onChange(formatDateTimeValue(v));
                      }
                    }}
                    value={
                      field.value
                        ? parseDateTimeValue(field.value).toDate()
                        : undefined
                    }
                  />
                </InputWrapper>
              );
            }}
          />
        </div>
        <div className="modal-footer py-4 d-flex flex-column align-items-start">
          <div className="d-flex justify-content-start mb-2">
            {!isStartButtonPressed && isPausedButtonPressed && (
              <button
                type="button"
                className="btn btn-success d-flex align-items-center justify-content-center me-2"
                onClick={handleStart}
              >
                <span className="me-2">Старт</span>
                <i className="bi bi-play-fill"></i>
              </button>
            )}

            {isStartButtonPressed && isPausedButtonPressed && (
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center justify-content-center me-2"
                onClick={handlePause}
              >
                <span className="me-2">Пауза</span>
                <i className="bi bi-pause"></i>
              </button>
            )}

            {(!isPausedButtonPressed || resultStatus?.status === 'paused') && (
              <button
                type="button"
                className="btn btn-warning d-flex align-items-center justify-content-center me-2"
                onClick={handleContinue}
              >
                <span className="me-2">Продолжить</span>
                <i className="bi bi-play-fill"></i>
              </button>
            )}

            <button
              type="button"
              className="btn btn-danger d-flex align-items-center justify-content-center me-2"
              onClick={handleStop}
            >
              <span className="me-2">Стоп</span>
              <i className="bi bi-stop"></i>
            </button>
          </div>

          <button
            type="submit"
            className="mt-5 px-5 btn btn-success w-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : emailCampaign?.id ? (
              'Сохранить'
            ) : (
              'Добавить'
            )}
          </button>
        </div>
        {data && (
          <div className="p-4">
            <h4 className="text-black mb-3">Статистика рассылки</h4>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Отправлено:</span>
              <span className="w-50">{data.sent}</span>
            </div>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Доставлено:</span>
              <span className="w-50">{data.delivered}</span>
            </div>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Прочитано:</span>
              <span className="w-50">{data.opened}</span>
            </div>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Переход по ссылке:</span>
              <span className="w-50">{data.clicked}</span>
            </div>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Отписались:</span>
              <span className="w-50">{data.unsubscribed}</span>
            </div>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Переподписались:</span>
              <span className="w-50">{data.subscribed}</span>
            </div>
            <div className="d-flex w-100 mb-2">
              <span className="w-50">Доставка не удалась:</span>
              <span className="w-50">
                {data.hard_bounced + data.soft_bounced}
              </span>
            </div>
            <div className="d-flex w-100">
              <span className="w-50">Спам:</span>
              <span className="w-50">{data.spam}</span>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};
